import { ReactNode, useEffect, useRef, useState } from 'react';
import { PropsWithChildren } from 'react';
import React from 'react';
import { debounce } from 'lodash-es';

import { Box, ResponsiveValue, Text, TextProps } from '@chakra-ui/react';

export type ClampedTextV2Props = PropsWithChildren<
  {
    noOfLines: ResponsiveValue<number>;
    renderButton: (props: { isClamped: boolean; onToggle: () => void }) => ReactNode;
  } & TextProps
>;

export const ClampedTextV2 = ({ children, noOfLines, renderButton, ...textProps }: ClampedTextV2Props) => {
  const [requiresClamping, setRequiresClamping] = useState(false);
  const [isClamped, setIsClamped] = useState(true);
  const originalTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function hasOverflow(element: HTMLDivElement | null) {
      if (!element) return false;
      return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
    }
    const originalText = originalTextRef.current;
    if (!originalText) return;

    const handleResize = debounce((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        const isOverflowing = hasOverflow(entry.target as HTMLDivElement);
        setRequiresClamping(isOverflowing);
      }
    }, 250);

    const observer = new ResizeObserver(handleResize);
    observer.observe(originalText);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Box position='relative'>
        {/* Note: This is a hack to get the original text height. It must inherit the same
            typography (fontSize/lineHeight/fontFamily/etc.) as the visible text below, otherwise
            it measures the wrong number of lines and clamps text that actually fits. */}
        <Text
          ref={originalTextRef}
          {...textProps}
          as='div'
          border={0}
          margin='-1px'
          noOfLines={noOfLines}
          opacity={0}
          padding={0}
          pointerEvents='none'
          position='absolute'
          sx={{
            ...textProps.sx,
            clip: 'rect(0, 0, 0, 0)',
          }}
          aria-hidden
        >
          {children}
        </Text>
        {/* Note: remove static positioning (i.e. position='relative') so that the following text renders above the dummy */}
        <Text {...textProps} noOfLines={isClamped ? noOfLines : undefined} position='relative'>
          {children}
        </Text>
      </Box>
      {requiresClamping && renderButton({ isClamped, onToggle: () => setIsClamped((prev) => !prev) })}
    </>
  );
};
